export const MCP_READ_SCOPE = "mcp.read";
export const MCP_SOURCE_CONTROL_READ_SCOPE = "mcp.source_control.read";
export const MCP_SOURCE_CONTROL_WRITE_SCOPE = "mcp.source_control.write";
export const MCP_ISSUES_READ_SCOPE = "mcp.issues.read";
export const MCP_ISSUES_WRITE_SCOPE = "mcp.issues.write";
export const MCP_INDEXING_READ_SCOPE = "mcp.indexing.read";
export const MCP_INDEXING_WRITE_SCOPE = "mcp.indexing.write";
export const MCP_DELIVERY_READ_SCOPE = "mcp.delivery.read";
export const MCP_DELIVERY_WRITE_SCOPE = "mcp.delivery.write";
export const MCP_DELIVERY_FORCE_SCOPE = "mcp.delivery.force";
export const MCP_ACTORS_READ_SCOPE = "mcp.actors.read";
export const MCP_ACTORS_WRITE_SCOPE = "mcp.actors.write";
export const MCP_CHANNEL_ENVIRONMENTS_READ_SCOPE =
  "mcp.channel_environments.read";
export const MCP_CHANNEL_ENVIRONMENTS_WRITE_SCOPE =
  "mcp.channel_environments.write";
export const MCP_SKILLS_READ_SCOPE = "mcp.skills.read";
export const MCP_SKILLS_WRITE_SCOPE = "mcp.skills.write";

/**
 * The scopes the arti dashboard MCP resource (`/public/mcp`) advertises. Frozen:
 * its protected-resource document is a published contract, so a scope for
 * another resource must not appear in it.
 */
export const DASHBOARD_MCP_RESOURCE_SCOPES = [
  MCP_READ_SCOPE,
  MCP_SOURCE_CONTROL_READ_SCOPE,
  MCP_SOURCE_CONTROL_WRITE_SCOPE,
  MCP_ISSUES_READ_SCOPE,
  MCP_ISSUES_WRITE_SCOPE,
  MCP_INDEXING_READ_SCOPE,
  MCP_INDEXING_WRITE_SCOPE,
  MCP_DELIVERY_READ_SCOPE,
  MCP_DELIVERY_WRITE_SCOPE,
  MCP_DELIVERY_FORCE_SCOPE,
  MCP_ACTORS_READ_SCOPE,
  MCP_ACTORS_WRITE_SCOPE,
  MCP_CHANNEL_ENVIRONMENTS_READ_SCOPE,
  MCP_CHANNEL_ENVIRONMENTS_WRITE_SCOPE,
] as const;

/** The scopes the Skill Mesh resource (`/public/skill-mesh/mcp`) advertises. */
export const SKILL_MESH_RESOURCE_SCOPES = [
  MCP_SKILLS_READ_SCOPE,
  MCP_SKILLS_WRITE_SCOPE,
] as const;

/**
 * Every scope a token may carry, across both MCP resources. This is the
 * authorization server's list and the `McpScope` domain -- not any one
 * resource's advertised set.
 */
export const MCP_SUPPORTED_SCOPES = [
  ...DASHBOARD_MCP_RESOURCE_SCOPES,
  ...SKILL_MESH_RESOURCE_SCOPES,
] as const;

export type McpScope = (typeof MCP_SUPPORTED_SCOPES)[number];
export type McpScopeCategory =
  | "actors"
  | "channel_environments"
  | "delivery"
  | "identity"
  | "indexing"
  | "metadata"
  | "skills";
export type McpScopeRisk = "force" | "read" | "write";

export interface McpScopeDefinition {
  category: McpScopeCategory;
  description: string;
  label: string;
  optional: boolean;
  risk: McpScopeRisk;
  scope: McpScope;
}

export interface NormalizeMcpScopeInputOptions {
  defaultScopes?: readonly McpScope[];
  splitOnWhitespace?: boolean;
}

export const MCP_SCOPE_CATEGORY_COPY: Record<
  McpScopeCategory,
  { description: string; title: string }
> = {
  actors: {
    description:
      "Actor scopes expose autonomous agent instances and their communication channels — letting clients spawn, inspect, message, and terminate actors.",
    title: "Actor Management",
  },
  channel_environments: {
    description:
      "Channel environment scopes expose persistent actor workspaces, sandbox settings, repository attachments, restart controls, and system-managed egress needed by attached repositories.",
    title: "Workspace Environments",
  },
  delivery: {
    description:
      "Delivery scopes expose in-flight execution state and guarded control actions across ARTI.",
    title: "Delivery Control",
  },
  identity: {
    description:
      "Identity scopes let the client establish who it is acting for in Arti.",
    title: "Workspace Identity",
  },
  indexing: {
    description:
      "Indexing scopes expose compact repository knowledge and the ability to refresh it on demand.",
    title: "Indexed Knowledge",
  },
  metadata: {
    description:
      "Metadata scopes keep the token observational, letting clients read repository and issue state.",
    title: "Connected Systems",
  },
  skills: {
    description:
      "Skill scopes expose the organization's shared Skill Mesh catalog of Claude Code skills, and the ability to publish and revise them.",
    title: "Shared Skills",
  },
};

export const MCP_SCOPE_DEFINITIONS: readonly McpScopeDefinition[] = [
  {
    category: "identity",
    description:
      "Lets the connector verify your user identity and organization context inside Arti.",
    label: "Read workspace identity",
    optional: false,
    risk: "read",
    scope: MCP_READ_SCOPE,
  },
  {
    category: "metadata",
    description:
      "Lets the connector inspect source control providers, the current repository allowlist, and remote repository or branch metadata available to this workspace.",
    label: "Read source control data",
    optional: true,
    risk: "read",
    scope: MCP_SOURCE_CONTROL_READ_SCOPE,
  },
  {
    category: "metadata",
    description:
      "Lets the connector create repositories through connected source control providers and edit the repository allowlist ARTI uses for planning, execution, and indexing.",
    label: "Control source control",
    optional: true,
    risk: "write",
    scope: MCP_SOURCE_CONTROL_WRITE_SCOPE,
  },
  {
    category: "metadata",
    description:
      "Lets the connector inspect issue providers and project metadata, search issues, and read compact issue details for this workspace.",
    label: "Read issue data",
    optional: true,
    risk: "read",
    scope: MCP_ISSUES_READ_SCOPE,
  },
  {
    category: "metadata",
    description:
      "Lets the connector make issue-tracking changes such as creating issues, updating fields, transitioning status, and posting comments.",
    label: "Control issue tracking",
    optional: true,
    risk: "write",
    scope: MCP_ISSUES_WRITE_SCOPE,
  },
  {
    category: "indexing",
    description:
      "Lets the connector inspect indexed repository knowledge, including indexing runs, compact insight summaries, and snippet references.",
    label: "Read indexing knowledge",
    optional: true,
    risk: "read",
    scope: MCP_INDEXING_READ_SCOPE,
  },
  {
    category: "indexing",
    description:
      "Lets the connector trigger or resume repository indexing runs for this workspace.",
    label: "Control indexing runs",
    optional: true,
    risk: "write",
    scope: MCP_INDEXING_WRITE_SCOPE,
  },
  {
    category: "delivery",
    description:
      "Lets the connector inspect ARTI delivery state, including tasks, flows, locks, and worker health for this workspace.",
    label: "Read delivery state",
    optional: true,
    risk: "read",
    scope: MCP_DELIVERY_READ_SCOPE,
  },
  {
    category: "delivery",
    description:
      "Lets the connector request guarded delivery actions such as task cancellation and execution retries. High-impact actions still require confirmation.",
    label: "Control delivery actions",
    optional: true,
    risk: "write",
    scope: MCP_DELIVERY_WRITE_SCOPE,
  },
  {
    category: "delivery",
    description:
      "Lets the connector perform force-style delivery recovery actions such as releasing stale locks after explicit confirmation.",
    label: "Force recovery actions",
    optional: true,
    risk: "force",
    scope: MCP_DELIVERY_FORCE_SCOPE,
  },
  {
    category: "actors",
    description:
      "Lets the connector list actor instances, inspect their state, list channels, and read channel message history for this workspace.",
    label: "Read actor instances and channels",
    optional: true,
    risk: "read",
    scope: MCP_ACTORS_READ_SCOPE,
  },
  {
    category: "actors",
    description:
      "Lets the connector spawn and kill actor instances, create channels, and send messages to actors.",
    label: "Control actors and channels",
    optional: true,
    risk: "write",
    scope: MCP_ACTORS_WRITE_SCOPE,
  },
  {
    category: "channel_environments",
    description:
      "Lets the connector inspect channel environments, repository attachments, sandbox resource settings, and system-managed egress rules.",
    label: "Read channel environments",
    optional: true,
    risk: "read",
    scope: MCP_CHANNEL_ENVIRONMENTS_READ_SCOPE,
  },
  {
    category: "channel_environments",
    description:
      "Lets the connector edit channel environment settings, restart or archive workspaces, and attach or detach repositories. Organization-admin authorization is still required.",
    label: "Control channel environments",
    optional: true,
    risk: "write",
    scope: MCP_CHANNEL_ENVIRONMENTS_WRITE_SCOPE,
  },
  {
    category: "skills",
    description:
      "Lets the connector list, search, and read the shared Claude Code skills published in this organization's Skill Mesh.",
    label: "Read shared skills",
    optional: true,
    risk: "read",
    scope: MCP_SKILLS_READ_SCOPE,
  },
  {
    category: "skills",
    description:
      "Lets the connector publish new shared skills and commit new versions of existing ones. Write also grants read.",
    label: "Publish shared skills",
    optional: true,
    risk: "write",
    scope: MCP_SKILLS_WRITE_SCOPE,
  },
];

const MCP_SCOPE_MAP = new Map<McpScope, McpScopeDefinition>(
  MCP_SCOPE_DEFINITIONS.map((definition) => [definition.scope, definition]),
);

const MCP_SUPPORTED_SCOPE_SET = new Set<string>(MCP_SUPPORTED_SCOPES);

export function getMcpScopeDefinition(scope: McpScope): McpScopeDefinition {
  const definition = MCP_SCOPE_MAP.get(scope);
  if (!definition) {
    throw new Error(`Unsupported MCP scope '${scope}'.`);
  }
  return definition;
}

export function isMcpScope(scope: string): scope is McpScope {
  return MCP_SUPPORTED_SCOPE_SET.has(scope);
}

export function normalizeMcpScopeInput(
  scopes: readonly string[] | undefined,
  options: NormalizeMcpScopeInputOptions = {},
): string[] {
  const rawScopes = scopes ?? options.defaultScopes ?? [MCP_READ_SCOPE];
  const normalizedEntries = options.splitOnWhitespace
    ? rawScopes.flatMap((scope) => scope.split(/\s+/))
    : [...rawScopes];

  return Array.from(
    new Set(normalizedEntries.map((scope) => scope.trim()).filter(Boolean)),
  );
}
